export const errorType = {
  NO_RETRIABLE_UNEXPECTED_ERROR: 'NO_RETRIABLE_UNEXPECTED_ERROR',
  RETRIABLE_UNEXPECTED_ERROR: 'RETRIABLE_UNEXPECTED_ERROR',
  CLIENT_FIELDS_ERROR: 'CLIENT_FIELDS_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  WARNING_ERROR: 'WARNING_ERROR'
} as const

export type ErrorType = (typeof errorType)[keyof typeof errorType]
export type Error = {
  code: ErrorCode
  category: ErrorType
  fields?: ErrorField[]
  message?: string
  /** Extra details of the error (e.g. `remainingAttempts` for PIN_INVALID). */
  data?: Record<string, unknown>
}
export type ErrorField = {
  name: string
  message: string
}
export type ErrorCode = (typeof errorCode)[keyof typeof errorCode]
export const errorCode = {
  // Session & Authentication
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  EXPIRED_JWT_TOKEN_ERROR: 'EXPIRED_JWT_TOKEN_ERROR',
  INVALID_JWT_TOKEN_ERROR: 'INVALID_JWT_TOKEN_ERROR',
  GENERIC_CLIENT_ERROR: 'GENERIC_CLIENT_ERROR',
  SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
  TOKEN_NOT_FOUND: 'TOKEN_NOT_FOUND',
  INVALID_SESSION: 'INVALID_SESSION',
  UNAUTHORIZED: 'UNAUTHORIZED',
  INVALID_JWT_TOKEN: 'INVALID_JWT_TOKEN',
  UNSUPPORTED_JWT_TOKEN: 'UNSUPPORTED_JWT_TOKEN',
  MALFORMED_JWT_TOKEN: 'MALFORMED_JWT_TOKEN',
  INVALID_JWT_SIGNATURE: 'INVALID_JWT_SIGNATURE',
  JWT_TOKEN_REVOKED: 'JWT_TOKEN_REVOKED',
  INVALID_REFRESH_TOKEN: 'INVALID_REFRESH_TOKEN',
  EXPIRED_JWT_TOKEN: 'EXPIRED_JWT_TOKEN',
  JWT_TOKEN_NOT_FOUND: 'JWT_TOKEN_NOT_FOUND',
  BAD_CREDENTIALS: 'BAD_CREDENTIALS',
  ACCOUNT_NOT_ACTIVATED: 'ACCOUNT_NOT_ACTIVATED',
  PASSWORD_CHANGE_REQUIRED: 'PASSWORD_CHANGE_REQUIRED',
  INVALID_KEY: 'INVALID_KEY',
  INVALID_PASSWORD: 'INVALID_PASSWORD',
  FORBIDDEN_ACTION: 'FORBIDDEN_ACTION',

  // PIN lock (MED-1621)
  PIN_REQUIRED: 'PIN_REQUIRED',
  SESSION_LOCKED: 'SESSION_LOCKED',
  PIN_INVALID: 'PIN_INVALID',
  PIN_ATTEMPTS_EXCEEDED: 'PIN_ATTEMPTS_EXCEEDED',
  PIN_ALREADY_SET: 'PIN_ALREADY_SET',
  PIN_INVALID_FORMAT: 'PIN_INVALID_FORMAT',
  PIN_RESET_OTP_INVALID: 'PIN_RESET_OTP_INVALID',
  PIN_RESET_RATE_LIMITED: 'PIN_RESET_RATE_LIMITED',
  PIN_CREATE_REAUTH_REQUIRED: 'PIN_CREATE_REAUTH_REQUIRED',
  SESSION_REUSE_DETECTED: 'SESSION_REUSE_DETECTED',

  // User Management
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  USER_ALREADY_EXISTS: 'USER_ALREADY_EXISTS',
  USER_SETTINGS_NOT_FOUND: 'USER_SETTINGS_NOT_FOUND',
  PATIENT_NOT_FOUND: 'PATIENT_NOT_FOUND',
  PATIENT_SETTINGS_NOT_FOUND: 'PATIENT_SETTINGS_NOT_FOUND',
  DOCTOR_NOT_FOUND: 'DOCTOR_NOT_FOUND',
  DOCTOR_SETTINGS_NOT_FOUND: 'DOCTOR_SETTINGS_NOT_FOUND',
  DOCTOR_SELF_REVIEW_NOT_ALLOWED: 'DOCTOR_SELF_REVIEW_NOT_ALLOWED',
  DOCTOR_NOT_ASSOCIATED_WITH_FACILITY: 'DOCTOR_NOT_ASSOCIATED_WITH_FACILITY',
  SECRETARY_NOT_FOUND: 'SECRETARY_NOT_FOUND',
  MANAGER_NOT_FOUND: 'MANAGER_NOT_FOUND',
  ROLE_NOT_FOUND: 'ROLE_NOT_FOUND',
  ROLE_NOT_MATCH: 'ROLE_NOT_MATCH',
  ROLE_IN_USE: 'ROLE_IN_USE',
  INVALID_VISIBILITY_SETTING: 'INVALID_VISIBILITY_SETTING',

  // Appointments
  APPOINTMENT_NOT_FOUND: 'APPOINTMENT_NOT_FOUND',
  APPOINTMENT_ALREADY_CANCELLED: 'APPOINTMENT_ALREADY_CANCELLED',
  INVALID_APPOINTMENT_STATUS_FOR_RESCHEDULE: 'INVALID_APPOINTMENT_STATUS_FOR_RESCHEDULE',
  CANCELLATION_RESTRICTED: 'CANCELLATION_RESTRICTED',
  CLINIC_APPOINTMENT_REQUIRES_FACILITY: 'CLINIC_APPOINTMENT_REQUIRES_FACILITY',
  CLINIC_HOME_APPOINTMENT_REQUIRES_LOCATION: 'CLINIC_HOME_APPOINTMENT_REQUIRES_LOCATION',
  MISSING_APPOINTMENT_WITH_DOCTOR: 'MISSING_APPOINTMENT_WITH_DOCTOR',

  // Time Slots & Availability
  TIME_SLOT_NOT_FOUND: 'TIME_SLOT_NOT_FOUND',
  TIME_SLOT_ALREADY_BOOKED: 'TIME_SLOT_ALREADY_BOOKED',
  TIME_SLOT_MIN_DURATION: 'TIME_SLOT_MIN_DURATION',
  TIME_SLOT_DURATION_EXCEEDS_RANGE: 'TIME_SLOT_DURATION_EXCEEDS_RANGE',
  TIME_SLOT_INVERSION: 'TIME_SLOT_INVERSION',
  TIME_SLOT_START_IN_PAST: 'TIME_SLOT_START_IN_PAST',
  TIME_SLOT_DOES_NOT_BELONG_TO_DOCTOR: 'TIME_SLOT_DOES_NOT_BELONG_TO_DOCTOR',
  TIME_SLOT_OUTSIDE_SECRETARY_FACILITY: 'TIME_SLOT_OUTSIDE_SECRETARY_FACILITY',
  TIME_SLOT_NOT_IN_FACILITY: 'TIME_SLOT_NOT_IN_FACILITY',
  AVAILABILITY_NOT_FOUND: 'AVAILABILITY_NOT_FOUND',
  AVAILABILITY_ALREADY_EXISTS: 'AVAILABILITY_ALREADY_EXISTS',
  AVAILABILITY_START_DATE_TOO_FAR_IN_FUTURE: 'AVAILABILITY_START_DATE_TOO_FAR_IN_FUTURE',
  AVAILABILITY_PARTIAL_CREATION_FAILURE: 'AVAILABILITY_PARTIAL_CREATION_FAILURE',
  MAX_WEEKS_AHEAD_LIMIT_EXCEEDED: 'MAX_WEEKS_AHEAD_LIMIT_EXCEEDED',
  END_DATE_REQUIRED: 'END_DATE_REQUIRED',
  END_DATE_BEFORE_START_DATE: 'END_DATE_BEFORE_START_DATE',

  // Specialties
  SPECIALITY_NOT_FOUND: 'SPECIALITY_NOT_FOUND',
  SPECIALITY_ALREADY_EXISTS: 'SPECIALITY_ALREADY_EXISTS',
  SPECIALTY_IN_USE: 'SPECIALTY_IN_USE',
  SPECIALTY_TRANSLATION_NOT_FOUND: 'SPECIALTY_TRANSLATION_NOT_FOUND',
  DUPLICATE_SPECIALTY_CODES_IN_REQUEST: 'DUPLICATE_SPECIALTY_CODES_IN_REQUEST',
  MAX_SPECIALTY_LIMIT_EXCEEDED: 'MAX_SPECIALTY_LIMIT_EXCEEDED',

  // Licence
  LICENCE_NOT_FOUND: 'LICENCE_NOT_FOUND',
  DOCTOR_HAS_UPCOMING_APPOINTMENTS: 'DOCTOR_HAS_UPCOMING_APPOINTMENTS',

  // Healthcare Facilities
  HEALTHCARE_FACILITY_NOT_FOUND: 'HEALTHCARE_FACILITY_NOT_FOUND',
  HEALTHCARE_FACILITY_IN_USE: 'HEALTHCARE_FACILITY_IN_USE',
  MISSING_HEALTHCARE_FACILITY_MANAGER: 'MISSING_HEALTHCARE_FACILITY_MANAGER',
  DUPLICATE_HEALTHCARE_FACILITY_PROFESSIONAL: 'DUPLICATE_HEALTHCARE_FACILITY_PROFESSIONAL',

  // Services & Care
  PROVIDED_SERVICE_NOT_FOUND: 'PROVIDED_SERVICE_NOT_FOUND',
  CARE_TYPE_NOT_FOUND: 'CARE_TYPE_NOT_FOUND',

  // Reviews & Reactions
  REVIEW_NOT_FOUND: 'REVIEW_NOT_FOUND',
  REACTION_NOT_FOUND: 'REACTION_NOT_FOUND',
  DUPLICATE_REACTION: 'DUPLICATE_REACTION',

  // Emergency Contacts
  EMERGENCY_CONTACT_NOT_FOUND: 'EMERGENCY_CONTACT_NOT_FOUND',
  DUPLICATE_EMERGENCY_CONTACT_PHONE_NUMBER: 'DUPLICATE_EMERGENCY_CONTACT_PHONE_NUMBER',

  // Locations
  USER_LOCATION_MISMATCH: 'USER_LOCATION_MISMATCH',
  MAX_LOCATION_LIMIT_REACHED: 'MAX_LOCATION_LIMIT_REACHED',
  INVALID_PRIMARY_LOCATION: 'INVALID_PRIMARY_LOCATION',

  // Validation & Payload
  UNEXPECTED_ERROR: 'UNEXPECTED_ERROR',
  INVALID_PAYLOAD: 'INVALID_PAYLOAD',
  INVALID_PARAMETER: 'INVALID_PARAMETER',
  CONSTRAINT_VIOLATION: 'CONSTRAINT_VIOLATION',
  INVALID_DATE_TIME: 'INVALID_DATE_TIME',
  ID_NULL: 'ID_NULL',
  STATS_INVALID_TIME_SELECTION: 'STATS_INVALID_TIME_SELECTION'
}

export const InvalidSessionErrorsCodes = [
  'SESSION_EXPIRED',
  'EXPIRED_JWT_TOKEN_ERROR',
  'SESSION_NOT_FOUND',
  'TOKEN_NOT_FOUND',
  'INVALID_TOKEN',
  'INVALID_JWT_TOKEN_ERROR',
  'INVALID_JWT_TOKEN',
  'UNSUPPORTED_JWT_TOKEN',
  'MALFORMED_JWT_TOKEN',
  'INVALID_JWT_SIGNATURE',
  'JWT_TOKEN_REVOKED',
  'INVALID_REFRESH_TOKEN',
  'JWT_TOKEN_NOT_FOUND',
  'INVALID_SESSION'
]

export const ExpiredSessionErrorsCodes = [
  'SESSION_EXPIRED',
  'EXPIRED_JWT_TOKEN_ERROR',
  'EXPIRED_JWT_TOKEN'
]

/**
 * Codes of the PIN lock (MED-1621). They are handled by the lock screen: they never open the
 * generic error modal and never trigger the 401/403 redirect to the login page.
 */
export const PinLockErrorCodes: string[] = [
  errorCode.PIN_REQUIRED,
  errorCode.SESSION_LOCKED,
  errorCode.PIN_INVALID,
  errorCode.PIN_ATTEMPTS_EXCEEDED,
  errorCode.PIN_ALREADY_SET,
  errorCode.PIN_INVALID_FORMAT,
  errorCode.PIN_RESET_OTP_INVALID,
  errorCode.PIN_RESET_RATE_LIMITED,
  errorCode.PIN_CREATE_REAUTH_REQUIRED,
  errorCode.SESSION_REUSE_DETECTED,
]
